import { useEffect, useRef, useState, type JSX } from 'react';

import { clearTimeoutIfExists } from 'libs/node';

import { useIntersection } from 'hooks/useIntersection';

type Props = {
  children: () => JSX.Element;
  fallback: () => JSX.Element;
  delay?: number;
  threshold?: number;
};

interface FallbackWrapperProps {
  fallback: () => JSX.Element;
  onIntersect: () => void;
  threshold?: number;
}

const FallbackWrapper = ({ fallback: Fallback, onIntersect, threshold }: FallbackWrapperProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const intersected = useIntersection(ref, { threshold });

  useEffect(() => {
    if (intersected) {
      onIntersect();
    }
  }, [intersected, onIntersect]);

  return (
    <div ref={ref}>
      <Fallback />
    </div>
  );
};

export const DynamicImport = ({ children, fallback, delay, threshold }: Props) => {
  const [intersected, setIntersected] = useState(false);
  const [delayCompleted, setDelayCompleted] = useState(delay === undefined);

  useEffect(() => {
    if (delay === undefined || delayCompleted || intersected === false) {
      return;
    }

    const timeout = setTimeout(() => {
      setDelayCompleted(true);
    }, delay);

    return () => {
      clearTimeoutIfExists(timeout);
    };
  }, [delay, delayCompleted, intersected]);

  if (intersected === false || delayCompleted === false) {
    return <FallbackWrapper fallback={fallback} onIntersect={() => setIntersected(true)} threshold={threshold} />;
  }

  return children();
};
